import React, { useEffect, useMemo, useState } from 'react';

import { DownloadIcon } from '@vlabs/icons';
import { estimateIcaoCompliance } from '@vlabs/shared/requests';
import { downloadJson } from '@vlabs/shared/utils';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectDynamicHandlerId } from '@vlabs/pages/handlers/selectors';

import { ResultSectionPanel } from '@vlabs/pages/checks/components/result-section-panel/ResultSectionPanel';

import { CheckTag } from '../../check-tag/CheckTag';
import { FaceQualityChecksTable, useTogglableTable } from '../result-items';
import type { SectionProps } from '../types';
import st from './ICAOSection.module.sass';
import { useChecksResultContext } from '../../checksResultContext';
import { Loader } from '../../loader/Loader';
import { EstimationError } from '../estimation-error';

function ICAOSection({
  file,
}: SectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const [results, setResults] = useState(undefined);
  const [resultsJson, setResultsJson] = useState<object | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const { updateCheckResult } = useChecksResultContext();

  const table = useMemo(() => {
    if (!results) return null;
    return (
      <FaceQualityChecksTable checks={results?.checks} />
    );
  }, [results]);
  const toglableTable = useTogglableTable({
    table,
  });

  const dynamicHandlerId = useSelector(selectDynamicHandlerId);

  useEffect(() => {
    if (!file) return;
    if (!dynamicHandlerId) return;
    estimateIcaoCompliance({
      file,
      handlerId: dynamicHandlerId,
      raw: true,
    }).then((resp) => {
      setResultsJson(resp);
      if ((resp?.events?.length ?? 0) === 0) setError(t('checks:error.no events'));
      else setResults(resp?.events?.[0]?.detections?.[0]?.samples?.face?.detection?.face_quality);
    }).catch(($error) => {
      if ($error.error_code !== 11038) throw $error;
      setError(t('checks:error.multiple faces') as unknown as string);
    });
  }, [file, dynamicHandlerId, t]);

  useEffect(() => {
    if (!results) return;
    updateCheckResult?.(results?.status === 1);
  }, [results, updateCheckResult]);

  const sectionChildren = useMemo(() => {
    if (error) return <EstimationError error={error} />;
    if (results && !!toglableTable.isOpen) return toglableTable.table;
    if (!results && !error) return <Loader />;
    return undefined;
  }, [error, results, toglableTable]);

  return (
    <ResultSectionPanel
      subtitle={(
        results ? (
          <div className={st.Subtitle}>
            <IconButton
              icon={<DownloadIcon />}
              kind="secondary"
              onClick={() => downloadJson(`${file.name}_icao`, resultsJson)}
              shape="round"
              size="small"
              variant="dimmed"
            />
            <div className={st.Tag}>
              <CheckTag
                kind={results.status}
                toggle={toglableTable.toggle}
                value={
                  results.status === 1
                    ? t('checks:estimations.common.ok')
                    : t('checks:estimations.common.error', { count: results.checks.filter((check) => check.result === 0).length })
                }
              />
            </div>
          </div>
        ) : undefined
      )}
      title={t('checks:estimations.icao.title')}
    >
      {sectionChildren}
    </ResultSectionPanel>
  );
}

export { ICAOSection };
